import { Text } from "react-native";
import { useTranslation } from "react-i18next";
import {
  draftRoundTypes,
  lostEffects,
  programRoundLabel,
  thresholdStall,
  type Draft,
  type ProgressionRule,
  type SlotRef,
} from "@heraion/shared";

import { ruleAt } from "./ruleCache";
import type { V3Styles } from "./v3Styles";

// What the rule sheet says before it writes (#351, brief §5.3-5.4): the test or
// deload effect a row loses, and a threshold whose load does not rise in the
// program's rounds. One plain line each; it warns, never blocks.

export function RuleWarnings({
  styles,
  draft,
  refs,
  names,
  rules,
  loadQuantum,
}: {
  styles: V3Styles;
  draft: Draft;
  refs: readonly SlotRef[];
  /** Each ref's exercise name, in `refs` order. */
  names: readonly string[];
  /** The rule each ref is about to get, in `refs` order. */
  rules: readonly ProgressionRule[];
  loadQuantum: number;
}) {
  const { t } = useTranslation();
  const types = draftRoundTypes(draft);
  const lost = { test: [] as string[], deload: [] as string[] };
  refs.forEach((r, i) => {
    const rec = ruleAt(draft, r.day, r.slot, loadQuantum);
    const next = rules[i];
    if (rec.kind !== "rule" || !next) return;
    const l = lostEffects(rec.rule, next, types);
    if (l.test) lost.test.push(names[i] ?? "");
    if (l.deload) lost.deload.push(names[i] ?? "");
  });
  const many = refs.length > 1;
  const lines: string[] = [];
  if (lost.test.length > 0) {
    lines.push(
      many
        ? t("editorV3.rule.lostTestMany", { names: lost.test.join(", "), defaultValue: "No AMRAP in the test round for: {{names}}." })
        : t("editorV3.rule.lostTest", { defaultValue: "No AMRAP in the test round." }),
    );
  }
  if (lost.deload.length > 0) {
    lines.push(
      many
        ? t("editorV3.rule.lostDeloadMany", { names: lost.deload.join(", "), defaultValue: "No deload for: {{names}}." })
        : t("editorV3.rule.lostDeload", { defaultValue: "No deload." }),
    );
  }
  // The anchors differ per row, the progression does not: the first rule speaks for all.
  const stall = rules[0] ? thresholdStall(rules[0], types, draft.cycleWeeks, { loadQuantum }) : undefined;
  if (stall) {
    lines.push(
      stall.firstRise === null
        ? t("editorV3.rule.stallNever", { defaultValue: "The load never goes up in these rounds." })
        : t("editorV3.rule.stallAt", {
            round: programRoundLabel(stall.firstRise + 1),
            defaultValue: "The load does not go up in these rounds. First rise: {{round}}.",
          }),
    );
  }
  return (
    <>
      {lines.map((line) => (
        <Text key={line} style={styles.muted}>
          {line}
        </Text>
      ))}
    </>
  );
}
